import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixQuickpagesV1Page from './quickpages-v1-page-pages.http.js';
// @ts-ignore
import { transformSDKImageToRESTImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/** QuickPage Page */
export interface Page extends PageOriginOneOf {
  /**
   * The ID of a page from which the page was created.
   * @format GUID
   * @immutable
   */
  originPageId?: string;
  /**
   * The ID of a template from which the page was created.
   * @format GUID
   * @immutable
   */
  templateId?: string;
  /**
   * Page ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number. Incremented on every update.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the Page was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the Page was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Date and time the Page was published or scheduled to be published depending on the status.
   * @readonly
   */
  publishedDate?: Date | null;
  /**
   * Date and time the Page was unpublished or scheduled to be unpublished depending on the status.
   * @readonly
   */
  unpublishedDate?: Date | null;
  /**
   * Page display name.
   * @minLength 5
   * @maxLength 100
   */
  name?: string | null;
  /**
   * The status of the Page.
   * @readonly
   */
  status?: StatusWithLiterals;
  /** An image specifically designated for display when the page URL is shared on social media platforms. */
  socialPreviewImage?: string;
  /** The small icon displayed in the browser tab, bookmarks, and potentially other areas. */
  favicon?: string;
  /**
   * The title of the page, visible in the browser tab and used as the primary clickable link in search engine results.
   * @maxLength 100
   */
  title?: string | null;
  /**
   * A brief summary of the page's content, often displayed below the title in search engine results.
   * @maxLength 300
   */
  description?: string | null;
  /** A flag to indicate whether search engines should be allowed to index this page and include it in their search results. */
  searchEngineIndexingEnabled?: boolean;
  /**
   * The route information for the page.
   * @readonly
   */
  pageRoute?: PageRoute;
  /** Data Extensions */
  extendedFields?: ExtendedFields;
  /** Tags */
  tags?: Tags;
}

/** @oneof */
export interface PageOriginOneOf {
  /**
   * The ID of a page from which the page was created.
   * @format GUID
   * @immutable
   */
  originPageId?: string;
  /**
   * The ID of a template from which the page was created.
   * @format GUID
   * @immutable
   */
  templateId?: string;
}

export enum Status {
  /** Unknown status. */
  UNKNOWN_STATUS = 'UNKNOWN_STATUS',
  /** The Page is draft. */
  DRAFT = 'DRAFT',
  /** The Page is published. */
  PUBLISHED = 'PUBLISHED',
  /** The Page is scheduled to be published. */
  SCHEDULED = 'SCHEDULED',
  /** The Page is archived. */
  ARCHIVED = 'ARCHIVED',
}

/** @enumType */
export type StatusWithLiterals =
  | Status
  | 'UNKNOWN_STATUS'
  | 'DRAFT'
  | 'PUBLISHED'
  | 'SCHEDULED'
  | 'ARCHIVED';

export interface PageRoute {
  /**
   * The route ID from the Routes API
   * @internal
   * @format GUID
   * @readonly
   */
  routeId?: string | null;
  /**
   * Page slug used in the URL.
   * @minLength 3
   * @maxLength 100
   */
  slug?: string;
  /** The URL type for the page, determining how it's accessed (subdomain or path). */
  urlType?: TypeWithLiterals;
}

export enum Type {
  /** The page is accessed via a path. */
  PATH = 'PATH',
}

/** @enumType */
export type TypeWithLiterals = Type | 'PATH';

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

/**
 * Common object for tags.
 * Should be use as in this example:
 * message Foo {
 * string id = 1;
 * ...
 * Tags tags = 5
 * }
 *
 * example of taggable entity
 * {
 * id: "123"
 * tags: {
 * tags: {
 * tag_ids:["11","22"]
 * },
 * private_tags: {
 * tag_ids: ["33", "44"]
 * }
 * }
 * }
 */
export interface Tags {
  /** Tags that require an additional permission in order to access them, normally not given to site members or visitors. */
  privateTags?: TagList;
  /** Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors. */
  tags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

/** Page Published. */
export interface PagePublished {
  /** Page. */
  page?: Page;
}

/** Page Unpublished. */
export interface PageUnpublished {
  /** Page. */
  page?: Page;
}

/** Page Archived. */
export interface PageArchived {
  /** Page. */
  page?: Page;
}

/** Page Archived. */
export interface PageUnarchived {
  /** Page. */
  page?: Page;
}

/** Page Scheduled. */
export interface PageScheduled {
  /** Page. */
  page?: Page;
}

export interface CreatePageRequest {
  /** Page to be created. */
  page: Page;
}

export interface CreatePageResponse {
  /** The created Page. */
  page?: Page;
}

export interface GetPageRequest {
  /**
   * ID of the Page to retrieve.
   * @format GUID
   */
  pageId: string;
}

export interface GetPageResponse {
  /** The requested Page. */
  page?: Page;
}

export interface UpdatePageRequest {
  /** Page to be updated, may be partial. */
  page: Page;
  /**
   * Set of fields to update.
   *
   * Fields that aren't included in `fieldMask.paths` are ignored.
   * @internal
   */
  fieldMask?: string[];
}

export interface UpdatePageResponse {
  /** Updated Page. */
  page?: Page;
}

export interface SetPageRouteRequest {
  /**
   * ID of the Page.
   * @format GUID
   */
  pageId: string;
  /** Route to add. */
  pageRoute: PageRoute;
}

export interface SetPageRouteResponse {}

export interface RemovePageRouteRequest {
  /**
   * ID of the Page.
   * @format GUID
   */
  pageId: string;
}

export interface RemovePageRouteResponse {}

export interface PublishPageRequest {
  /**
   * ID of the Page to publish.
   * @format GUID
   */
  pageId: string;
  /** Date and time when the page should be unpublished. */
  unpublishedDate?: Date | null;
}

export interface PublishPageResponse {
  /** Published Page. */
  page?: Page;
}

export interface UnpublishPageRequest {
  /**
   * ID of the Page to unpublish.
   * @format GUID
   */
  pageId: string;
}

export interface UnpublishPageResponse {
  /** Unpublished Page. */
  page?: Page;
}

export interface ArchivePageRequest {
  /**
   * ID of the Page to archive.
   * @format GUID
   */
  pageId: string;
}

export interface ArchivePageResponse {
  /** Archived Page. */
  page?: Page;
}

export interface UnarchivePageRequest {
  /**
   * ID of the Page to archive.
   * @format GUID
   */
  pageId: string;
}

export interface UnarchivePageResponse {
  /** Archived Page. */
  page?: Page;
}

export interface SchedulePageRequest {
  /**
   * ID of the Page to schedule.
   * @format GUID
   */
  pageId: string;
  /** Date and time when the page should be published. */
  publishedDate?: Date | null;
  /** Date and time when the page should be unpublished. */
  unpublishedDate?: Date | null;
}

export interface SchedulePageResponse {
  /** Scheduled Page. */
  page?: Page;
}

export interface DeletePageRequest {
  /**
   * Id of the Page to delete.
   * @format GUID
   */
  pageId: string;
}

export interface DeletePageResponse {}

export interface QueryPagesRequest {
  /** WQL expression. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryPagesResponse {
  /** List of Pages. */
  pages?: Page[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface BulkUpdatePageTagsRequest {
  /**
   * List of NileProtoTagsEntities that their tags will update.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  pageIds: string[];
  /** List of Tags to assign */
  assignTags?: Tags;
  /** List of Tags to unAssign */
  unassignTags?: Tags;
}

export interface BulkUpdatePageTagsResponse {
  /**
   * Results
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdatePageTagsResult[];
  /** Metadata regarding the bulk update operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkUpdatePageTagsResult {
  /** Metadata regarding the specific single update operation */
  itemMetadata?: ItemMetadata;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdatePageTagsByFilterRequest {
  /** Filter */
  filter: Record<string, any> | null;
  /** List of Tags to assign */
  assignTags?: Tags;
  /** List of Tags to unAssign */
  unassignTags?: Tags;
}

export interface BulkUpdatePageTagsByFilterResponse {
  /**
   * Job ID
   * @format GUID
   */
  jobId?: string;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type BulkUpdatePageTagsApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkUpdatePageTagsByFilterApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};

/**
 * Creates a Page.
 * @param page - Page to be created.
 * @internal
 * @documentationMaturity preview
 * @requiredField page
 * @permissionId QUICK_PAGES.PAGE_CREATE
 * @returns The created Page.
 * @fqn wix.quickpages.pages.v1.Pages.CreatePage
 */
export async function createPage(
  page: Page
): Promise<
  NonNullablePaths<
    Page,
    | `originPageId`
    | `templateId`
    | `status`
    | `searchEngineIndexingEnabled`
    | `pageRoute.slug`
    | `pageRoute.urlType`
    | `tags.privateTags.tagIds`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ page: page }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'page.socialPreviewImage' }, { path: 'page.favicon' }],
      },
    ]
  );

  const reqOpts = ambassadorWixQuickpagesV1Page.createPage(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'page.socialPreviewImage' },
            { path: 'page.favicon' },
          ],
        },
      ])
    )?.page!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { page: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['page']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a Page.
 * @param pageId - ID of the Page to retrieve.
 * @internal
 * @documentationMaturity preview
 * @requiredField pageId
 * @permissionId QUICK_PAGES.PAGE_READ
 * @returns The requested Page.
 * @fqn wix.quickpages.pages.v1.Pages.GetPage
 */
export async function getPage(
  pageId: string
): Promise<
  NonNullablePaths<
    Page,
    | `originPageId`
    | `templateId`
    | `status`
    | `searchEngineIndexingEnabled`
    | `pageRoute.slug`
    | `pageRoute.urlType`
    | `tags.privateTags.tagIds`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ pageId: pageId });

  const reqOpts = ambassadorWixQuickpagesV1Page.getPage(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'page.socialPreviewImage' },
            { path: 'page.favicon' },
          ],
        },
      ])
    )?.page!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { pageId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['pageId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates a Page.
 *
 *
 * Each time the page is updated,
 * `revision` increments by 1.
 * The current `revision` must be passed when updating the page.
 * This ensures you're working with the latest page
 * and prevents unintended overwrites.
 * @param _id - Page ID.
 * @internal
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField page
 * @requiredField page.revision
 * @permissionId QUICK_PAGES.PAGE_UPDATE
 * @returns Updated Page.
 * @fqn wix.quickpages.pages.v1.Pages.UpdatePage
 */
export async function updatePage(
  _id: string,
  page: NonNullablePaths<UpdatePage, `revision`, 2>
): Promise<
  NonNullablePaths<
    Page,
    | `originPageId`
    | `templateId`
    | `status`
    | `searchEngineIndexingEnabled`
    | `pageRoute.slug`
    | `pageRoute.urlType`
    | `tags.privateTags.tagIds`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ page: { ...page, id: _id } }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'page.socialPreviewImage' }, { path: 'page.favicon' }],
      },
    ]
  );

  const reqOpts = ambassadorWixQuickpagesV1Page.updatePage(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'page.socialPreviewImage' },
            { path: 'page.favicon' },
          ],
        },
      ])
    )?.page!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { page: '$[1]' },
        explicitPathsToArguments: { 'page.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'page']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdatePage {
  /**
   * The ID of a page from which the page was created.
   * @format GUID
   * @immutable
   */
  originPageId?: string;
  /**
   * The ID of a template from which the page was created.
   * @format GUID
   * @immutable
   */
  templateId?: string;
  /**
   * Page ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number. Incremented on every update.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the Page was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the Page was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Date and time the Page was published or scheduled to be published depending on the status.
   * @readonly
   */
  publishedDate?: Date | null;
  /**
   * Date and time the Page was unpublished or scheduled to be unpublished depending on the status.
   * @readonly
   */
  unpublishedDate?: Date | null;
  /**
   * Page display name.
   * @minLength 5
   * @maxLength 100
   */
  name?: string | null;
  /**
   * The status of the Page.
   * @readonly
   */
  status?: StatusWithLiterals;
  /** An image specifically designated for display when the page URL is shared on social media platforms. */
  socialPreviewImage?: string;
  /** The small icon displayed in the browser tab, bookmarks, and potentially other areas. */
  favicon?: string;
  /**
   * The title of the page, visible in the browser tab and used as the primary clickable link in search engine results.
   * @maxLength 100
   */
  title?: string | null;
  /**
   * A brief summary of the page's content, often displayed below the title in search engine results.
   * @maxLength 300
   */
  description?: string | null;
  /** A flag to indicate whether search engines should be allowed to index this page and include it in their search results. */
  searchEngineIndexingEnabled?: boolean;
  /**
   * The route information for the page.
   * @readonly
   */
  pageRoute?: PageRoute;
  /** Data Extensions */
  extendedFields?: ExtendedFields;
  /** Tags */
  tags?: Tags;
}

/**
 * Add a route for a page
 * @param pageId - ID of the Page.
 * @param pageRoute - Route to add.
 * @internal
 * @documentationMaturity preview
 * @requiredField pageId
 * @requiredField pageRoute
 * @permissionId QUICK_PAGES.SET_ROUTE
 * @fqn wix.quickpages.pages.v1.Pages.SetPageRoute
 */
export async function setPageRoute(
  pageId: string,
  pageRoute: PageRoute
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    pageId: pageId,
    pageRoute: pageRoute,
  });

  const reqOpts = ambassadorWixQuickpagesV1Page.setPageRoute(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { pageId: '$[0]', pageRoute: '$[1]' },
        singleArgumentUnchanged: false,
      },
      ['pageId', 'pageRoute']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Remove a route for a page
 * @param pageId - ID of the Page.
 * @internal
 * @documentationMaturity preview
 * @requiredField pageId
 * @permissionId QUICK_PAGES.REMOVE_ROUTE
 * @fqn wix.quickpages.pages.v1.Pages.RemovePageRoute
 */
export async function removePageRoute(pageId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ pageId: pageId });

  const reqOpts = ambassadorWixQuickpagesV1Page.removePageRoute(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { pageId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['pageId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Publish a Page.
 * @param pageId - ID of the Page to publish.
 * @internal
 * @documentationMaturity preview
 * @requiredField pageId
 * @permissionId QUICK_PAGES.PAGE_PUBLISH
 * @fqn wix.quickpages.pages.v1.Pages.PublishPage
 */
export async function publishPage(
  pageId: string,
  options?: PublishPageOptions
): Promise<
  NonNullablePaths<
    PublishPageResponse,
    | `page.originPageId`
    | `page.templateId`
    | `page.status`
    | `page.searchEngineIndexingEnabled`
    | `page.pageRoute.slug`
    | `page.pageRoute.urlType`
    | `page.tags.privateTags.tagIds`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    pageId: pageId,
    unpublishedDate: options?.unpublishedDate,
  });

  const reqOpts = ambassadorWixQuickpagesV1Page.publishPage(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'page.socialPreviewImage' },
            { path: 'page.favicon' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          pageId: '$[0]',
          unpublishedDate: '$[1].unpublishedDate',
        },
        singleArgumentUnchanged: false,
      },
      ['pageId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface PublishPageOptions {
  /** Date and time when the page should be unpublished. */
  unpublishedDate?: Date | null;
}

/**
 * Unpublish a page.
 * @param pageId - ID of the Page to unpublish.
 * @internal
 * @documentationMaturity preview
 * @requiredField pageId
 * @permissionId QUICK_PAGES.PAGE_UNPUBLISH
 * @fqn wix.quickpages.pages.v1.Pages.UnpublishPage
 */
export async function unpublishPage(
  pageId: string
): Promise<
  NonNullablePaths<
    UnpublishPageResponse,
    | `page.originPageId`
    | `page.templateId`
    | `page.status`
    | `page.searchEngineIndexingEnabled`
    | `page.pageRoute.slug`
    | `page.pageRoute.urlType`
    | `page.tags.privateTags.tagIds`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ pageId: pageId });

  const reqOpts = ambassadorWixQuickpagesV1Page.unpublishPage(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'page.socialPreviewImage' },
            { path: 'page.favicon' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { pageId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['pageId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Archive a Page.
 * @param pageId - ID of the Page to archive.
 * @internal
 * @documentationMaturity preview
 * @requiredField pageId
 * @permissionId QUICK_PAGES.PAGE_ARCHIVE
 * @fqn wix.quickpages.pages.v1.Pages.ArchivePage
 */
export async function archivePage(
  pageId: string
): Promise<
  NonNullablePaths<
    ArchivePageResponse,
    | `page.originPageId`
    | `page.templateId`
    | `page.status`
    | `page.searchEngineIndexingEnabled`
    | `page.pageRoute.slug`
    | `page.pageRoute.urlType`
    | `page.tags.privateTags.tagIds`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ pageId: pageId });

  const reqOpts = ambassadorWixQuickpagesV1Page.archivePage(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'page.socialPreviewImage' },
            { path: 'page.favicon' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { pageId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['pageId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Unarchive a Page.
 * @param pageId - ID of the Page to archive.
 * @internal
 * @documentationMaturity preview
 * @requiredField pageId
 * @permissionId QUICK_PAGES.PAGE_UNARCHIVE
 * @fqn wix.quickpages.pages.v1.Pages.UnarchivePage
 */
export async function unarchivePage(
  pageId: string
): Promise<
  NonNullablePaths<
    UnarchivePageResponse,
    | `page.originPageId`
    | `page.templateId`
    | `page.status`
    | `page.searchEngineIndexingEnabled`
    | `page.pageRoute.slug`
    | `page.pageRoute.urlType`
    | `page.tags.privateTags.tagIds`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ pageId: pageId });

  const reqOpts = ambassadorWixQuickpagesV1Page.unarchivePage(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'page.socialPreviewImage' },
            { path: 'page.favicon' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { pageId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['pageId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Schedule a Page for publishing at a future date.
 * @param pageId - ID of the Page to schedule.
 * @internal
 * @documentationMaturity preview
 * @requiredField pageId
 * @permissionId QUICK_PAGES.PAGE_PUBLISH
 * @fqn wix.quickpages.pages.v1.Pages.SchedulePage
 */
export async function schedulePage(
  pageId: string,
  options?: SchedulePageOptions
): Promise<
  NonNullablePaths<
    SchedulePageResponse,
    | `page.originPageId`
    | `page.templateId`
    | `page.status`
    | `page.searchEngineIndexingEnabled`
    | `page.pageRoute.slug`
    | `page.pageRoute.urlType`
    | `page.tags.privateTags.tagIds`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    pageId: pageId,
    publishedDate: options?.publishedDate,
    unpublishedDate: options?.unpublishedDate,
  });

  const reqOpts = ambassadorWixQuickpagesV1Page.schedulePage(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'page.socialPreviewImage' },
            { path: 'page.favicon' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          pageId: '$[0]',
          publishedDate: '$[1].publishedDate',
          unpublishedDate: '$[1].unpublishedDate',
        },
        singleArgumentUnchanged: false,
      },
      ['pageId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface SchedulePageOptions {
  /** Date and time when the page should be published. */
  publishedDate?: Date | null;
  /** Date and time when the page should be unpublished. */
  unpublishedDate?: Date | null;
}

/**
 * Deletes a Page.
 * @param pageId - Id of the Page to delete.
 * @internal
 * @documentationMaturity preview
 * @requiredField pageId
 * @permissionId QUICK_PAGES.PAGE_DELETE
 * @fqn wix.quickpages.pages.v1.Pages.DeletePage
 */
export async function deletePage(pageId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ pageId: pageId });

  const reqOpts = ambassadorWixQuickpagesV1Page.deletePage(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { pageId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['pageId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of Pages, given the provided [paging, filtering, and sorting][1].
 *
 * Up to 1,000 Pages can be returned per request.
 *
 * To learn how to query Pages, see [API Query Language][2].
 *
 * [1]: https://dev.wix.com/api/rest/getting-started/sorting-and-paging
 * [2]: https://dev.wix.com/api/rest/getting-started/api-query-language
 * @internal
 * @documentationMaturity preview
 * @permissionId QUICK_PAGES.PAGE_READ
 * @fqn wix.quickpages.pages.v1.Pages.QueryPages
 */
export function queryPages(): PagesQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<Page, 'CURSOR', QueryPagesRequest, QueryPagesResponse>({
    func: async (payload: QueryPagesRequest) => {
      const reqOpts = ambassadorWixQuickpagesV1Page.queryPages(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryPagesRequest['query']) => {
      const args = [query, {}] as [QueryPagesRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({ data }: HttpResponse<QueryPagesResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [
          {
            transformFn: transformRESTImageToSDKImage,
            paths: [
              { path: 'pages.socialPreviewImage' },
              { path: 'pages.favicon' },
            ],
          },
        ])
      );

      return {
        items: transformedData?.pages,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface PagesQueryResult extends QueryCursorResult {
  items: Page[];
  query: PagesQueryBuilder;
  next: () => Promise<PagesQueryResult>;
  prev: () => Promise<PagesQueryResult>;
}

export interface PagesQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName: '_id' | '_createdDate' | 'status',
    value: any
  ) => PagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName: '_id' | '_createdDate' | 'status',
    value: any
  ) => PagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (propertyName: '_id' | '_createdDate', value: any) => PagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (propertyName: '_id' | '_createdDate', value: any) => PagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (propertyName: '_id' | '_createdDate', value: any) => PagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (propertyName: '_id' | '_createdDate', value: any) => PagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   * @documentationMaturity preview
   */
  startsWith: (propertyName: '_id', value: string) => PagesQueryBuilder;
  /** @documentationMaturity preview */
  in: (
    propertyName: '_id' | '_createdDate' | 'status',
    value: any
  ) => PagesQueryBuilder;
  /** @documentationMaturity preview */
  exists: (
    propertyName: '_id' | '_createdDate' | 'status',
    value: boolean
  ) => PagesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  ascending: (
    ...propertyNames: Array<'_id' | '_createdDate' | 'status'>
  ) => PagesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  descending: (
    ...propertyNames: Array<'_id' | '_createdDate' | 'status'>
  ) => PagesQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => PagesQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => PagesQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<PagesQueryResult>;
}

/**
 * @hidden
 * @fqn wix.quickpages.pages.v1.Pages.QueryPages
 * @requiredField query
 */
export async function typedQueryPages(
  query: PageQuery
): Promise<
  NonNullablePaths<
    QueryPagesResponse,
    | `pages`
    | `pages.${number}.originPageId`
    | `pages.${number}.templateId`
    | `pages.${number}.status`
    | `pages.${number}.searchEngineIndexingEnabled`
    | `pages.${number}.pageRoute.slug`
    | `pages.${number}.pageRoute.urlType`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts = ambassadorWixQuickpagesV1Page.queryPages(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'pages.socialPreviewImage' },
            { path: 'pages.favicon' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface PageQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['_createdDate', '_id', 'status'];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<Page, PageQuerySpec>;
export type PageQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]` 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: /*#__PURE__*/ createQueryUtils<Page, PageQuerySpec, PageQuery>(),
};

/**
 * Synchronously update tags on multiple Pages, by list of Pages ids
 * A tag that appears both in the list of assign and unassign tags, will be assigned
 * @param pageIds - List of NileProtoTagsEntities that their tags will update.
 * @internal
 * @documentationMaturity preview
 * @requiredField pageIds
 * @permissionId QUICK_PAGES.PAGE_UPDATE_TAGS
 * @fqn wix.quickpages.pages.v1.Pages.BulkUpdatePageTags
 */
export async function bulkUpdatePageTags(
  pageIds: string[],
  options?: BulkUpdatePageTagsOptions
): Promise<
  NonNullablePaths<
    BulkUpdatePageTagsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __applicationErrorsType?: BulkUpdatePageTagsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    pageIds: pageIds,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts = ambassadorWixQuickpagesV1Page.bulkUpdatePageTags(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          pageIds: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['pageIds', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdatePageTagsOptions {
  /** List of Tags to assign */
  assignTags?: Tags;
  /** List of Tags to unAssign */
  unassignTags?: Tags;
}

/**
 * Asynchronously update tags on multiple Pages, by provided filter
 * An empty filter will update all Pages
 * A tag that appears both in the list of assign and unassign tags, will be assigned
 * @param filter - Filter
 * @internal
 * @documentationMaturity preview
 * @requiredField filter
 * @permissionId QUICK_PAGES.PAGE_UPDATE_TAGS
 * @fqn wix.quickpages.pages.v1.Pages.BulkUpdatePageTagsByFilter
 */
export async function bulkUpdatePageTagsByFilter(
  filter: Record<string, any>,
  options?: BulkUpdatePageTagsByFilterOptions
): Promise<
  NonNullablePaths<BulkUpdatePageTagsByFilterResponse, `jobId`, 2> & {
    __applicationErrorsType?: BulkUpdatePageTagsByFilterApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: filter,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixQuickpagesV1Page.bulkUpdatePageTagsByFilter(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          filter: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['filter', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdatePageTagsByFilterOptions {
  /** List of Tags to assign */
  assignTags?: Tags;
  /** List of Tags to unAssign */
  unassignTags?: Tags;
}
